import React, { FunctionComponent, useState, useEffect, useCallback } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import Section from 'components/UI/Section';
import { Box, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { Staff } from 'model/Staff';
import Button from 'components/UI/Button';
import AddCommissions from './UI/AddCommissions';
import { useDispatch, useSelector } from 'react-redux';
import { useApiClientWrapper, useAuth } from 'hooks';
import { CommissionsStatus, disableThunk, fetchByStaffMemberThunk } from '@spike/commissions-action';
import { CommissionPlan } from '@spike/commission-model';
import { RootState } from 'store';
import { Link } from 'react-router-dom';
import { CommissionItem } from './UI/CommisionItem';
import ViewCommission from './UI/ViewCommission';
import useNonInitialEffect from '@versiondos/hooks';
import { MobileDivider } from 'components/UI/MobileDivider';
import { OWNER_ID } from '@spike/model';
import { Drawer } from 'components/UI/V3/Drawer/Drawer';

interface CommissionsProps {
    className?: string;
    member?: Staff;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        fullContainer: {
            //width: '100%',
            height: '100%'
        },
        container: {
            width: '100%',
            display: 'flex',
            flexWrap: 'wrap',
            padding: '0px 16px',

            [theme.breakpoints.up('md')]: {
                paddingLeft: '38px',
                paddingRight: '38px'
            },
            [theme.breakpoints.up('xl')]: {
                paddingLeft: '50px',
                paddingRight: '50px'
            }
        },
        title: {
            [theme.breakpoints.down('lg')]: {
                fontSize: 20,
                fontWeight: 600
            }
        },
        subtitle: {
            fontSize: 15,
            fontWeight: 400,
            color: '#000',
            textAlign: 'left',
            [theme.breakpoints.up('md')]: {
                marginTop: 20
            },
            [theme.breakpoints.down('sm')]: {
                textAlign: 'left'
            }
        },
        subtitleWithoutButton: {
            [theme.breakpoints.up('md')]: {
                marginBottom: 20
            }
        },
        button: {
            height: 40,
            fontWeight: 100,
            marginTop: 25,
            marginBottom: '25px',

            [theme.breakpoints.down('sm')]: {
                marginBottom: 35
            },

            [theme.breakpoints.down('lg')]: {
                'padding': '0px 16px',
                '& span': {
                    fontSize: 14,
                    fontWeight: 600
                }
            },
            [theme.breakpoints.up('xl')]: {
                height: '40px',
                marginBottom: '25px'
            }
        },
        commissionListTitle: {
            fontSize: '16px',
            fontWeight: 600
        },
        commisionListTitleContainer: {
            width: '100% !important',
            padding: '0px 16px',

            [theme.breakpoints.up('md')]: {
                paddingLeft: '38px',
                paddingRight: '38px'
            },
            [theme.breakpoints.up('xl')]: {
                paddingLeft: '50px',
                paddingRight: '50px'
            }
        },
        commissionListItemContainer: {
            width: '100% !important',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
        },
        comissionListItemChipContainer: {
            width: '25%'
        },
        commisionListItemActionTextContainer: {
            width: '25%'
        },
        commisionListItemDescContainer: {
            width: '50%'
        },
        commissionListItemNotLast: {
            borderBottom: '1px #D4D4D4 solid'
        }
    })
);

export const Commissions: FunctionComponent<CommissionsProps> = props => {
    const classes = useStyles();

    const [viewCommission, setViewCommission] = useState<CommissionPlan | undefined>(undefined);
    const [editCommissions, setEditCommissions] = useState<boolean>(false);
    const [saving, setSaving] = useState(false);
    const [activeSelected, setActiveSelected] = useState<boolean>(false);
    const commissionStatus = useSelector<RootState, CommissionsStatus>(state => state.commissions.status);

    const auth = useAuth();
    const isOwner = auth.user!.role.id === OWNER_ID;

    const dispatch = useDispatch();

    const apiClientWrapper = useApiClientWrapper();

    const commissionPlans = useSelector<RootState, CommissionPlan[]>(
        state => state.commissions.staffMemberCommissionPlans
    );

    useEffect(() => {
        if (props.member) {
            dispatch(fetchByStaffMemberThunk(apiClientWrapper, props.member.id!));
        }
    }, []);

    useNonInitialEffect(() => {
        if (commissionStatus === CommissionsStatus.SaveSuccess) {
            dispatch(fetchByStaffMemberThunk(apiClientWrapper, props.member!.id!));
        }
        if (commissionStatus === CommissionsStatus.DisableSuccess) {
            const updated = commissionPlans.find(cp => cp.id === viewCommission?.id);
            if (updated) {
                setViewCommission(updated);
                setActiveSelected(updated.active);
            }
            dispatch(fetchByStaffMemberThunk(apiClientWrapper, props.member!.id!));
        }
    }, [commissionStatus]);

    const saveHandler = useCallback(() => {
        setEditCommissions(false);
    }, []);

    const handleView = useCallback(
        (uuid: string, active: boolean) => {
            const plan = commissionPlans.find(cp => cp.uuid === uuid);
            setViewCommission(plan);
            setActiveSelected(active);
        },
        [commissionPlans]
    );

    const closeViewCommission = useCallback(() => setViewCommission(undefined), []);

    const toggleViewCommission = useCallback(() => {
        if (viewCommission?.id) {
            setActiveSelected(false);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            dispatch(disableThunk(apiClientWrapper as any, viewCommission.id));
        }
    }, [viewCommission?.id, apiClientWrapper]);

    const commissionReportComponent = isOwner ? (
        <Link
            to="/payments#COMMISSIONS"
            style={{ fontWeight: 600 }}
        >
            Commission Report.
        </Link>
    ) : (
        <>Commission Report.</>
    );

    return (
        <>
            <Box className={clsx(classes.fullContainer, props.className)}>
                <MobileDivider />
                <Section
                    title="Commissions"
                    titleClassName={classes.title}
                >
                    <Box className={classes.container}>
                        {props.member?.id ? (
                            <Typography className={classes.subtitle}>
                                Set how much {props.member?.person.firstName} can make. Commission is assigned to team
                                members based on sale attribution, and is summarized in the {commissionReportComponent}{' '}
                            </Typography>
                        ) : (
                            <Typography className={clsx(classes.subtitle, classes.subtitleWithoutButton)}>
                                You’ll be able to add commissions once this team member has been created. After saving,
                                return to this profile to set commission plans.
                            </Typography>
                        )}
                        {props.member?.id && (
                            <Box>
                                <Button
                                    id="services_button_add_service"
                                    color="green"
                                    variant="primary"
                                    className={classes.button}
                                    onClick={() => setEditCommissions(true)}
                                    label="+ Add Commissions"
                                />
                            </Box>
                        )}
                    </Box>

                    {commissionPlans && commissionPlans.length > 0 && (
                        <>
                            <Box className={classes.commisionListTitleContainer}>
                                <Typography className={classes.commissionListTitle}>Active Commission</Typography>
                            </Box>
                            <Box className={classes.container}>
                                {commissionPlans.map((commissionPlanItem, index) => {
                                    return (
                                        <CommissionItem
                                            key={commissionPlanItem.uuid}
                                            commissionPlan={commissionPlanItem}
                                            active={commissionPlanItem.active}
                                            isLast={index === commissionPlans.length - 1}
                                            onView={handleView}
                                        ></CommissionItem>
                                    );
                                })}
                            </Box>
                        </>
                    )}
                </Section>
            </Box>
            {editCommissions && (
                <Drawer
                    onClose={() => {
                        setSaving(false);
                        setEditCommissions(false);
                    }}
                >
                    <AddCommissions
                        title={'Add Commissions'}
                        saving={saving}
                        onSave={saveHandler}
                        onClose={() => {
                            setSaving(false);
                            setEditCommissions(false);
                        }}
                        member={props.member}
                    />
                </Drawer>
            )}

            {viewCommission && (
                <Drawer onClose={closeViewCommission}>
                    <ViewCommission
                        onClose={closeViewCommission}
                        active={activeSelected}
                        commissionPlan={viewCommission}
                        onToggle={toggleViewCommission}
                    />
                </Drawer>
            )}
        </>
    );
};

export default Commissions;
